import React, { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';

export type ThemeMode = 'dark' | 'light';

interface ThemeContextValue {
    theme: ThemeMode;
    isDark: boolean;
    setTheme: (theme: ThemeMode) => void;
    toggleTheme: () => void;
}

// A chave versionada inicia todos na nova identidade marfim sem apagar a
// preferência que será escolhida daqui em diante.
const THEME_STORAGE_KEY = '@chamados:theme:v2';
const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

/**
 * Lê somente valores conhecidos para impedir que um localStorage inválido
 * deixe a aplicação sem tema. Marfim é o tema padrão; dark é uma alternativa.
 */
const readStoredTheme = (): ThemeMode => {
    try {
        return localStorage.getItem(THEME_STORAGE_KEY) === 'dark' ? 'dark' : 'light';
    } catch {
        return 'light';
    }
};

/** Aplica também a cor do navegador/PWA para combinar com o tema selecionado. */
const applyThemeToDocument = (theme: ThemeMode) => {
    document.documentElement.dataset.theme = theme;
    document.documentElement.style.colorScheme = theme;
    document.querySelector('meta[name="theme-color"]')
        ?.setAttribute('content', theme === 'light' ? '#f7f3ea' : '#0b0f1a');
};

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
    const [theme, setThemeState] = useState<ThemeMode>(readStoredTheme);

    const setTheme = useCallback((nextTheme: ThemeMode) => {
        setThemeState(nextTheme);
        applyThemeToDocument(nextTheme);
        try {
            localStorage.setItem(THEME_STORAGE_KEY, nextTheme);
        } catch {
            // O tema continua funcionando durante a sessão mesmo com storage bloqueado.
        }
    }, []);

    const toggleTheme = useCallback(() => {
        setTheme(theme === 'dark' ? 'light' : 'dark');
    }, [setTheme, theme]);

    useEffect(() => {
        applyThemeToDocument(theme);
    }, [theme]);

    useEffect(() => {
        const synchronizeTabs = (event: StorageEvent) => {
            if (event.key !== THEME_STORAGE_KEY) return;
            const nextTheme: ThemeMode = event.newValue === 'dark' ? 'dark' : 'light';
            setThemeState(nextTheme);
            applyThemeToDocument(nextTheme);
        };
        window.addEventListener('storage', synchronizeTabs);
        return () => window.removeEventListener('storage', synchronizeTabs);
    }, []);

    return (
        <ThemeContext.Provider value={{
            theme,
            isDark: theme === 'dark',
            setTheme,
            toggleTheme,
        }}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) throw new Error('useTheme deve ser utilizado dentro de ThemeProvider');
    return context;
};

